import { action, computed, makeObservable, observable } from 'mobx'

import {
  DEFAULT_ATTACHMENT_BAR_WIDTH,
  getAttachmentBarOccupiedWidth,
  getOpenAttachmentBarCount,
  getSidePanelContentMode,
  SidePanelContentMode,
  subscribeAttachmentBarPresence,
  subscribeSidePanelContentMode,
} from '@avantstay/backoffice-core'

export enum SidePanelType {
  addBookingTags = 'ADD_BOOKING_TAGS',
  addNewBookingGuest = 'ADD_NEW_BOOKING_GUEST',
  addNewSecurityDeposit = 'ADD_NEW_DEPOSIT',
  arrivalsChecklist = 'ARRIVALS_CHECKLIST',
  guestInboxStayDetails = 'GUEST_INBOX_STAY_DETAILS',
  guestInboxV2StayDetails = 'GUEST_INBOX_V2_STAY_DETAILS',
  bookingAdjustment = 'BOOKING_ADJUSTMENTS',
  bookingCancellation = 'BOOKING_CANCELLATION',
  bookingCouponInfo = 'BOOKING_COUPON_INFO',
  bookingManageBalance = 'BOOKING_MANAGE_BALANCE',
  bookingModification = 'BOOKING_MODIFICATION',
  bookingPendingModification = 'BOOKING_PENDING_MODIFICATION',
  bookingReviewRequests = 'BOOKING_REVIEW_REQUESTS',
  bookingReviewSoliciting = 'BOOKING_REVIEW_SOLICITING',
  bookingSendItemizedReceipt = 'BOOKING_SEND_ITEMIZED_RECEIPT',
  bookingSendReceipt = 'BOOKING_SEND_RECEIPT',
  bookingsReviewsAiGeneratedSummary = 'BOOKINGS_REVIEWS_AI_GENERATED_SUMMARY',
  bulkCaseEdit = 'BULK_CASE_EDIT',
  bulkEditMarkups = 'BULK_EDIT_MARKUPS',
  bulkMarketAssignmentEdit = 'BULK_MARKET_ASSIGNMENT_EDIT',
  createGuest = 'CREATE_GUEST',
  caseDetails = 'CASE_DETAILS',
  claimBookingLead = 'CLAIM_BOOKING_LEAD',
  completeDeferredStripePayment = 'COMPLETE_DEFERRED_STRIPE_PAYMENT',
  defaultMarkupsHistory = 'DEFAULT_MARKUPS_HISTORY',
  editCase = 'EDIT_CASE',
  editDefaultMarkups = 'EDIT_DEFAULT_MARKUPS',
  emailInvoiceDraftCase = 'EMAIL_INVOICE_DRAFT_CASE',
  emailInvoiceRelatedCases = 'EMAIL_INVOICE_RELATED_CASES',
  expensesDetails = 'EXPENSES_DETAILS',
  guestInfo = 'GUEST_INFO',
  homeownerDetails = 'HOMEOWNER_DETAILS',
  hotelStopSellRule = 'HOTEL_STOP_SELL_RULE',
  hotelTapeChartReservationInfo = 'HOTEL_TAPE_CHART_RESERVATION_INFO',
  lighthouseUserDetails = 'LIGHTHOUSE_USER_DETAILS',
  markupsHistory = 'MARKUPS_HISTORY',
  opsReviewMbrDrillDown = 'OPS_REVIEW_MBR_DRILL_DOWN',
  otaListingSyncStatus = 'OTA_LISTING_SYNC_STATUS',
  ownerRestrictionRuleHistory = 'OWNER_RESTRICTION_RULE_HISTORY',
  ownerStatementsBooking = 'OWNER_STATEMENTS_BOOKING',
  paymentAndServicesAdjustments = 'PAYMENT_AND_SERVICES_ADJUSTMENTS',
  paymentAndServicesCancelAdjustment = 'PAYMENT_AND_SERVICES_CANCEL_ADJUSTMENT',
  paymentAndServicesCharge = 'PAYMENT_AND_SERVICES_CHARGE',
  paymentAndServicesEditCancellationFee = 'PAYMENT_AND_SERVICES_EDIT_CANCELLATION_FEE',
  paymentAndServicesEditDeposit = 'PAYMENT_AND_SERVICES_EDIT_DEPOSIT',
  paymentAndServicesEditServiceRate = 'PAYMENT_AND_SERVICES_EDIT_SERVICE_RATE',
  paymentAndServicesRefund = 'PAYMENT_AND_SERVICES_REFUND',
  paymentAndServicesSaveNewCard = 'PAYMENT_AND_SERVICES_SAVE_NEW_CARD',
  propertiesAirbnbTripIssuesHistory = 'PROPERTIES_AIRBNB_TRIP_ISSUES_HISTORY',
  propertyReviewBreakdown = 'PROPERTY_REVIEW_BREAKDOWN',
  rateGuestReview = 'RATE_GUEST_REVIEW',
  replyGuestReview = 'REPLY_GUEST_REVIEW',
  requestGuestReview = 'REQUEST_GUEST_REVIEW',
  retryPayment = 'RETRY_PAYMENT',
  reviewMessagesSent = 'REVIEW_MESSAGES_SENT',
  seasonGroupProperties = 'SEASON_GROUP_PROPERTIES',
  seasonalCancellationPolicyHistory = 'SEASONAL_CANCELLATION_POLICY_HISTORY',
  seasons = 'SEASONS',
  seasonsSeasonGroups = 'SEASONS_SEASON_GROUPS',
  staffProperties = 'STAFF_PROPERTIES',
  tapeChartEditBlock = 'TAPE_CHART_EDIT_BLOCK',
  tapeChartPropertyInfo = 'TAPE_CHART_PROPERTY_INFO',
  tapeChartReservationInfo = 'TAPE_CHART_RESERVATION_INFO',
  tapeChartViewAllBookings = 'TAPE_CHART_VIEW_ALL_BOOKINGS', // used to present all search results (search in filters by booking id)
  tapeChartViewBlock = 'TAPE_CHART_VIEW_BLOCK',
  tapeChartViewMultipleBlocks = 'TAPE_CHART_VIEW_MULTIPLE_BLOCKS',
  taxesAndFeesForm = 'TAXES_AND_FEES_FORM',
  updateGuestInfo = 'UPDATE_GUEST_INFO',
  valueAddedServicesManagePayment = 'VALUE_ADDED_SERVICES_MANAGE_PAYMENT',
  valueAddedServicesNewServices = 'VALUE_ADDED_SERVICES_NEW_SERVICES',
  vendorDetails = 'VENDOR_DETAILS',
  vendorRateDetails = 'VENDOR_RATE_DETAILS',
}

export interface SidePanelOptions {
  screenHasSecondaryMenu?: boolean
}

class _SidePanelStore {
  // ------------ Observables
  // User preference (persisted in localStorage): panels either push the page
  // content aside or overlay it. Toggled from the AttachmentBar header button.
  contentMode: SidePanelContentMode = getSidePanelContentMode()
  currentSidePanel: SidePanelType | undefined = undefined
  hasSidePanel: boolean = false
  // Panels mounted directly through <AttachmentBar> (not registered here) still
  // count as open — with the right-edge width they occupy — so the layout can
  // push content aside for them too.
  openAttachmentBarCount: number = getOpenAttachmentBarCount()
  attachmentBarsOccupiedWidth: number = getAttachmentBarOccupiedWidth()
  screenHasSecondaryMenu: boolean = false
  unmounting: boolean = false

  constructor() {
    makeObservable(this, {
      contentMode: observable,
      currentSidePanel: observable,
      hasSidePanel: observable,
      openAttachmentBarCount: observable,
      attachmentBarsOccupiedWidth: observable,
      screenHasSecondaryMenu: observable,
      unmounting: observable,
      hasOpenPanels: computed,
      panelsOccupiedWidth: computed,
      setContentMode: action.bound,
      syncAttachmentBarPresence: action.bound,
      setCurrentSidePanel: action.bound,
      close: action.bound,
    })

    subscribeSidePanelContentMode(this.setContentMode)
    subscribeAttachmentBarPresence(this.syncAttachmentBarPresence)
  }

  // Any side panel open — store-driven or directly mounted.
  get hasOpenPanels() {
    return this.hasSidePanel || this.openAttachmentBarCount > 0
  }

  // Width the page content should clear in 'push' mode: the widest strip the
  // open panels occupy. Falls back to the default panel width while a store
  // panel is still lazy-loading and no bar has reported yet.
  get panelsOccupiedWidth() {
    return this.attachmentBarsOccupiedWidth || DEFAULT_ATTACHMENT_BAR_WIDTH
  }

  setContentMode(mode: SidePanelContentMode) {
    this.contentMode = mode
  }

  syncAttachmentBarPresence() {
    this.openAttachmentBarCount = getOpenAttachmentBarCount()
    this.attachmentBarsOccupiedWidth = getAttachmentBarOccupiedWidth()
  }

  // ---------------- Actions
  setCurrentSidePanel(sidePanel?: SidePanelType, options?: SidePanelOptions) {
    // If there is an existing side panel a new side panel is passed, maintain it open and switch content
    if (this.currentSidePanel && !!sidePanel) {
      this.currentSidePanel = sidePanel
      // Keep the current top offset when switching content, unless the caller overrides it
      this.screenHasSecondaryMenu = options?.screenHasSecondaryMenu ?? this.screenHasSecondaryMenu
      return
    }

    // If there is a side panel open and no new side panel is passed move to unmount and reset the states
    if (this.hasSidePanel && sidePanel == null) {
      this.unmounting = true

      // Set current side panel after initial animation change
      // (screenHasSecondaryMenu is kept so the panel doesn't jump during the slide-out)
      setTimeout(() => {
        this.currentSidePanel = sidePanel
      }, 100)
    } else {
      // Open new side panel
      this.unmounting = false
      this.currentSidePanel = sidePanel
      this.screenHasSecondaryMenu = options?.screenHasSecondaryMenu ?? false
    }

    this.hasSidePanel = !!sidePanel
  }

  close() {
    this.setCurrentSidePanel(undefined)
  }
}

export const SidePanelStore = new _SidePanelStore()
